if (!customElements.get('hero-slideshow')) {
  class HeroSlideshow extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.sectionId = this.getAttribute('data-section-id');
      this.autoplayEnabled = this.getAttribute('data-autoplay') === 'true';
      this.autoplaySpeed = Number(this.getAttribute('data-autoplay-speed')) || 5000;
      this.isSingleSlide = this.hasAttribute('data-single-slide');
      this.isMuted = true;
      this.mediaQuery = window.matchMedia('(min-width: 769px)');
      this.playToken = 0;
      this.restartTimer = null;

      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.onSlideChange = this.onSlideChange.bind(this);
      this.onSoundClick = this.onSoundClick.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.onReachEnd = this.onReachEnd.bind(this);
    }

    connectedCallback() {
      this.soundButton = this.querySelector('[data-slideshow-sound]');
      this.labels = {
        mute: this.soundButton?.dataset.labelMute || 'Mute video',
        unmute: this.soundButton?.dataset.labelUnmute || 'Unmute video',
      };

      if (this.soundButton) {
        this.soundButton.addEventListener('click', this.onSoundClick);
      }

      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.onMediaChange);
      } else {
        this.mediaQuery.addListener(this.onMediaChange);
      }

      this.waitForSwiper(() => this.swiperInit());
      document.addEventListener('shopify:section:load', this.onSectionLoad);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:load', this.onSectionLoad);

      if (this.soundButton) {
        this.soundButton.removeEventListener('click', this.onSoundClick);
      }

      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.onMediaChange);
      } else {
        this.mediaQuery.removeListener(this.onMediaChange);
      }

      this.clearRestartTimer();
      this.pauseAllVideos();
      this.swiperDestroy();
    }

    onSectionLoad(event) {
      if (event.detail && event.detail.sectionId === this.sectionId) {
        this.swiperDestroy();
        this.waitForSwiper(() => this.swiperInit());
      }
    }

    onMediaChange() {
      if (!this.swiper) return;
      this.syncVideos();
    }

    clearRestartTimer() {
      if (this.restartTimer) {
        window.clearTimeout(this.restartTimer);
        this.restartTimer = null;
      }
    }

    waitForSwiper(callback, attempts = 0) {
      if (typeof window.Swiper !== 'undefined') {
        callback();
        return;
      }

      if (attempts > 50) {
        console.warn('Swiper library not loaded for hero-slideshow');
        return;
      }

      window.setTimeout(() => this.waitForSwiper(callback, attempts + 1), 100);
    }

    swiperInit() {
      const swiperEl = this.querySelector('.swiper');
      if (!swiperEl || swiperEl.classList.contains('swiper-initialized')) return;

      const prevEl = this.querySelector('[data-swiper-prev]');
      const nextEl = this.querySelector('[data-swiper-next]');
      const paginationEl = this.querySelector('[data-swiper-pagination]');
      const hasVideos = this.querySelector('video.slideshow__video') !== null;

      // Avoid loop when videos exist — Swiper clones slides and duplicates video decode work.
      const options = {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 500,
        loop: !this.isSingleSlide && !hasVideos,
        watchOverflow: true,
        resistanceRatio: 0.65,
        a11y: {
          enabled: true,
        },
        on: {
          init: () => this.syncVideos(),
          slideChangeTransitionStart: this.onSlideChange,
          slideChangeTransitionEnd: (swiper) => this.prefetchNearbyVideos(swiper),
          reachEnd: this.onReachEnd,
        },
      };

      if (prevEl && nextEl) {
        options.navigation = {
          nextEl,
          prevEl,
        };
      }

      if (paginationEl) {
        options.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }

      if (this.autoplayEnabled && !this.isSingleSlide) {
        options.autoplay = {
          delay: this.autoplaySpeed,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
          stopOnLastSlide: false,
        };
      }

      this.swiper = new window.Swiper(swiperEl, options);
    }

    onReachEnd() {
      if (!this.autoplayEnabled || this.isSingleSlide || !this.swiper) return;
      if (this.swiper.params.loop) return;

      this.clearRestartTimer();
      this.restartTimer = window.setTimeout(() => {
        if (this.swiper && !this.swiper.destroyed && this.swiper.isEnd) {
          this.swiper.slideTo(0);
        }
      }, this.autoplaySpeed);
    }

    onSlideChange() {
      this.clearRestartTimer();
      this.syncVideos();
    }

    onSoundClick(event) {
      event.preventDefault();
      event.stopPropagation();

      this.isMuted = !this.isMuted;
      this.updateSoundButton();

      this.getActiveVisibleVideos().forEach((video) => {
        video.muted = this.isMuted;
        if (!this.isMuted && video.paused) {
          this.playVideo(video);
        }
      });
    }

    isDesktop() {
      return this.mediaQuery.matches;
    }

    getVisibleVideos(slide) {
      if (!slide) return [];

      const videos = Array.from(slide.querySelectorAll('video.slideshow__video'));
      const desktop = this.isDesktop();

      return videos.filter((video) => {
        if (video.classList.contains('slideshow__video--desktop')) return desktop;
        if (video.classList.contains('slideshow__video--mobile')) return !desktop;
        return true;
      });
    }

    getActiveSlide() {
      return this.querySelector('.swiper-slide-active') || this.querySelector('.slideshow__slide');
    }

    getActiveVisibleVideos() {
      return this.getVisibleVideos(this.getActiveSlide());
    }

    pauseAllVideos() {
      this.querySelectorAll('video.slideshow__video').forEach((video) => {
        video.pause();
      });
    }

    playVideo(video) {
      if (!video) return;
      // Hero videos are decorative motion; honour a reduced-motion preference and leave the poster up.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      video.muted = this.isMuted;
      video.defaultMuted = this.isMuted;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      const token = ++this.playToken;

      const tryPlay = () => {
        if (token !== this.playToken) return;
        if (!video.isConnected) return;

        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      };

      if (video.readyState >= 2) {
        tryPlay();
        return;
      }

      const onReady = () => {
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('canplay', onReady);
        tryPlay();
      };

      video.addEventListener('loadeddata', onReady);
      video.addEventListener('canplay', onReady);

      // Only kick a load if nothing is buffered yet
      if (video.networkState === HTMLMediaElement.NETWORK_EMPTY || video.readyState === 0) {
        try {
          video.load();
        } catch (error) {
          // Ignore abort errors from rapid slide changes
        }
      }
    }

    syncVideos() {
      const activeSlide = this.getActiveSlide();
      const activeVideos = this.getVisibleVideos(activeSlide);
      const activeSet = new Set(activeVideos);

      this.querySelectorAll('video.slideshow__video').forEach((video) => {
        if (activeSet.has(video)) return;
        if (!video.paused) {
          video.pause();
        }
      });

      activeVideos.forEach((video) => this.playVideo(video));
      this.updateSoundButton(activeVideos.length > 0);
    }

    prefetchNearbyVideos(swiper) {
      if (!swiper) return;

      const indexes = [swiper.activeIndex + 1, swiper.activeIndex - 1].filter(
        (index) => index >= 0 && index < swiper.slides.length
      );

      indexes.forEach((index) => {
        const slide = swiper.slides[index];
        this.getVisibleVideos(slide).forEach((video) => {
          if (video.getAttribute('preload') === 'none') {
            video.setAttribute('preload', 'metadata');
            video.preload = 'metadata';
          }
        });
      });
    }

    updateSoundButton(forceVisible) {
      if (!this.soundButton) return;

      const activeSlide = this.getActiveSlide();
      const isVideoSlide = activeSlide?.dataset.slideType === 'video';
      const visible = typeof forceVisible === 'boolean' ? forceVisible : isVideoSlide;

      this.soundButton.classList.toggle('hidden', !visible);
      this.soundButton.setAttribute('aria-pressed', String(this.isMuted));
      this.soundButton.setAttribute('aria-label', this.isMuted ? this.labels.unmute : this.labels.mute);

      const mutedIcon = this.soundButton.querySelector('.slideshow__sound-icon--muted');
      const unmutedIcon = this.soundButton.querySelector('.slideshow__sound-icon--unmuted');

      mutedIcon?.classList.toggle('hidden', !this.isMuted);
      unmutedIcon?.classList.toggle('hidden', this.isMuted);
    }

    swiperDestroy() {
      this.clearRestartTimer();
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }

  customElements.define('hero-slideshow', HeroSlideshow);
}
